import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { AiHandoff } from '../components/AiHandoff';
import { Sparkles, Mic, Image as ImageIcon, ArrowRight, Car, Truck, Home as HomeIcon, Package, Wrench, Snowflake, HardHat, Briefcase, Forklift } from 'lucide-react';
import { detectServiceFromPrompt, setActiveService, ServiceId } from '../lib/serviceData';
import { useAuth } from '../lib/authStore';
const categories: {
  id: ServiceId;
  name: string;
  icon: any;
  desc: string;
  price: string;
  color: string;
}[] = [{
  id: 'recovery',
  name: 'Vehicle Recovery',
  icon: Wrench,
  desc: '24/7 towing & assistance',
  price: 'AED 150',
  color: 'bg-red-50 text-red-600'
}, {
  id: 'carrier',
  name: 'Car Carrier',
  icon: Car,
  desc: 'Intercity vehicle transport',
  price: 'AED 280',
  color: 'bg-blue-50 text-blue-600'
}, {
  id: 'home',
  name: 'Home Moving',
  icon: HomeIcon,
  desc: 'Full apartment relocation',
  price: 'AED 450',
  color: 'bg-emerald-50 text-emerald-600'
}, {
  id: 'furniture',
  name: 'Furniture Moving',
  icon: Package,
  desc: 'Single items & small loads',
  price: 'AED 180',
  color: 'bg-amber-50 text-amber-600'
}, {
  id: 'goods',
  name: 'Goods Transport',
  icon: Truck,
  desc: 'Commercial cargo delivery',
  price: 'AED 320',
  color: 'bg-indigo-50 text-indigo-600'
}, {
  id: 'heavy',
  name: 'Heavy / Lowbed',
  icon: Forklift,
  desc: 'Equipment & machinery',
  price: 'AED 1,400',
  color: 'bg-slate-100 text-slate-700'
}, {
  id: 'chiller',
  name: 'Chiller Truck',
  icon: Snowflake,
  desc: 'Temperature controlled',
  price: 'AED 380',
  color: 'bg-cyan-50 text-cyan-600'
}, {
  id: 'construction',
  name: 'Construction',
  icon: HardHat,
  desc: 'Site & material delivery',
  price: 'AED 780',
  color: 'bg-teal-50 text-teal-600'
}, {
  id: 'business',
  name: 'Business Deliveries',
  icon: Briefcase,
  desc: 'B2B logistics solutions',
  price: 'Custom',
  color: 'bg-purple-50 text-purple-600'
}];
const examplePrompts = ['Move my car from Dubai to Abu Dhabi', 'Need recovery for broken BMW in Marina', 'Move 2BR apartment from JVC to Downtown', 'Need lowbed for excavator tomorrow'];
export function Home() {
  const [prompt, setPrompt] = useState('');
  const [handoff, setHandoff] = useState<{
    prompt: string;
    service: ServiceId;
  } | null>(null);
  const navigate = useNavigate();
  const {
    user
  } = useAuth();
  // Categories we don't yet have providers for — when the prompt clearly asks
  // for one of these, route to the "service unavailable" page instead of the
  // normal booking flow. Used as a demo for new categories we may add later.
  const UNSUPPORTED = [{
    keywords: ['crane', 'crain', 'cranes', 'krane'],
    label: 'crane services'
  }];
  const goWithPrompt = (rawPrompt: string) => {
    const lower = rawPrompt.toLowerCase();
    const unsupported = UNSUPPORTED.find(u => u.keywords.some(k => new RegExp(`\\b${k}\\b`, 'i').test(lower)));
    if (unsupported) {
      navigate('/unavailable', {
        state: {
          prompt: rawPrompt,
          category: unsupported.label
        }
      });
      return;
    }
    const service = detectServiceFromPrompt(rawPrompt);
    setActiveService(service, rawPrompt);
    // Show the futuristic AI handoff overlay; it will navigate when complete.
    setHandoff({
      prompt: rawPrompt,
      service
    });
  };
  const goWithCategory = (cat: (typeof categories)[number]) => {
    setActiveService(cat.id);
    navigate('/booking', {
      state: {
        service: cat.id
      }
    });
  };
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (prompt.trim()) goWithPrompt(prompt.trim());
  };
  return <>
      <AnimatePresence data-id="element-1566">
        {handoff && <AiHandoff prompt={handoff.prompt} onComplete={() => {
        navigate('/booking', {
          state: {
            prompt: handoff.prompt,
            service: handoff.service,
            preWarmed: true
          }
        });
      }} data-id="element-1567" />}
      </AnimatePresence>
      <div className="min-h-[calc(100vh-5rem)] flex flex-col" data-id="element-1568">
        {/* Hero Section */}
        <section className="relative pt-20 pb-32 overflow-hidden" data-id="element-1569">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-gradient-to-b from-brand-50/80 to-transparent rounded-full blur-3xl -z-10 pointer-events-none" data-id="element-1570" />

          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1571">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} data-id="element-1572">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-gray-200 shadow-sm mb-8" data-id="element-1573">
                <Sparkles className="w-4 h-4 text-brand-500" data-id="element-1574" />
                <span className="text-sm font-medium text-gray-600" data-id="element-1575">
                  Pair2 AI Logistics Engine
                </span>
              </div>

              <h1 className="text-5xl md:text-7xl font-display font-bold tracking-tight text-gray-900 mb-6" data-id="element-1576">
                The intelligent way to <br data-id="element-1577" />
                <span className="orange-gradient-text" data-id="element-1578">move anything.</span>
              </h1>

              <p className="text-xl text-gray-500 mb-12 max-w-2xl mx-auto font-light" data-id="element-1579">
                Describe what you need to move, and our AI will instantly match
                you with the perfect transport solution across the UAE.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="max-w-3xl mx-auto relative z-10" data-id="element-1580">
              <form onSubmit={handleSearch} className="relative group" data-id="element-1581">
                <div className="absolute -inset-1 bg-gradient-to-r from-brand-400 to-brand-600 rounded-[2rem] blur opacity-20 group-hover:opacity-30 transition duration-500" data-id="element-1582" />
                <div className="relative bg-white rounded-[2rem] shadow-xl p-2 flex items-center border border-gray-100" data-id="element-1583">
                  <div className="pl-6 pr-4 py-4 flex-1" data-id="element-1584">
                    <input type="text" value={prompt} onChange={e => setPrompt(e.target.value)} placeholder="Describe what you want to move today..." className="w-full text-lg outline-none bg-transparent placeholder-gray-400 text-gray-900" data-id="element-1585" />
                  </div>

                  <div className="flex items-center gap-2 pr-2" data-id="element-1586">
                    <button type="button" className="p-3 text-gray-400 hover:text-brand-500 hover:bg-brand-50 rounded-full transition-colors" data-id="element-1587">
                      <Mic className="w-5 h-5" data-id="element-1588" />
                    </button>
                    <button type="button" className="p-3 text-gray-400 hover:text-brand-500 hover:bg-brand-50 rounded-full transition-colors" data-id="element-1589">
                      <ImageIcon className="w-5 h-5" data-id="element-1590" />
                    </button>
                    <button type="submit" className="ml-2 px-6 py-4 bg-gray-900 hover:bg-black text-white rounded-full font-medium flex items-center gap-2 transition-colors shadow-md" data-id="element-1591">
                      <span data-id="element-1592">Find</span>
                      <ArrowRight className="w-4 h-4" data-id="element-1593" />
                    </button>
                  </div>
                </div>
              </form>

              <div className="mt-8 flex flex-wrap justify-center gap-3" data-id="element-1594">
                {examplePrompts.map((ex, i) => <button key={i} onClick={() => {
                setPrompt(ex);
                goWithPrompt(ex);
              }} className="px-4 py-2 rounded-full bg-white border border-gray-200 text-sm text-gray-600 hover:border-brand-300 hover:text-brand-600 transition-colors shadow-sm" data-id="element-1595">
                    "{ex}"
                  </button>)}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="bg-white py-24 border-t border-gray-100 flex-1" data-id="element-1596">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1597">
            <div className="flex items-center justify-between mb-12" data-id="element-1598">
              <div data-id="element-1599">
                <h2 className="text-3xl font-display font-bold text-gray-900" data-id="element-1600">
                  Explore Services
                </h2>
                <p className="text-gray-500 mt-2" data-id="element-1601">
                  Instant booking for any transport need
                </p>
              </div>
              <button className="text-brand-600 font-medium hover:text-brand-700 flex items-center gap-1" data-id="element-1602">
                View all <ArrowRight className="w-4 h-4" data-id="element-1603" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6" data-id="element-1604">
              {categories.map((cat, i) => <motion.div key={cat.id} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4,
              delay: i * 0.05
            }} className="group relative bg-surface-100 rounded-3xl p-6 hover:bg-white border border-transparent hover:border-gray-200 hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all duration-300 cursor-pointer" onClick={() => goWithCategory(cat)} data-id="element-1605">
                  <div className="flex justify-between items-start mb-6" data-id="element-1606">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${cat.color}`} data-id="element-1607">
                      <cat.icon className="w-7 h-7" data-id="element-1608" />
                    </div>
                    <div className="flex items-center gap-1.5 bg-white px-3 py-1 rounded-full shadow-sm text-xs font-medium text-gray-600 border border-gray-100" data-id="element-1609">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse-slow" data-id="element-1610" />
                      Live
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-brand-600 transition-colors" data-id="element-1611">
                    {cat.name}
                  </h3>
                  <p className="text-gray-500 text-sm mb-6" data-id="element-1612">{cat.desc}</p>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-200/60" data-id="element-1613">
                    <div className="text-sm" data-id="element-1614">
                      <span className="text-gray-400" data-id="element-1615">From </span>
                      <span className="font-semibold text-gray-900" data-id="element-1616">
                        {cat.price}
                      </span>
                    </div>
                    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center group-hover:bg-brand-50 group-hover:text-brand-600 transition-colors" data-id="element-1617">
                      <ArrowRight className="w-4 h-4" data-id="element-1618" />
                    </div>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>
      </div>
    </>;
}